// frontend/src/pages/shops/BusinessLooksPage.js
// R1/R2 business portal view of looks across permitted business locations
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SidebarLayout from "./SidebarLayout";
import { FaImages, FaFilter, FaRedo, FaEye } from "react-icons/fa";

const STATUS_LABELS = {
  draft: { label: "Draft", color: "#888", bg: "#f3f4f6" },
  awaiting_consent: {
    label: "Awaiting Consent",
    color: "#f0a500",
    bg: "#fffbeb",
  },
  ready: { label: "Ready", color: "#3b82f6", bg: "#eff6ff" },
  published: { label: "Published", color: "#22c55e", bg: "#f0fdf4" },
  archived: { label: "Archived", color: "#6b7280", bg: "#f9fafb" },
  deleted: { label: "Deleted", color: "#dc2626", bg: "#fee2e2" },
};

const FILTER_TABS = [
  { key: "all", label: "All Looks" },
  { key: "published", label: "Published" },
  { key: "awaiting_consent", label: "Awaiting Consent" },
  { key: "archived", label: "Archived" },
];

function BusinessLooksPage() {
  const navigate = useNavigate();

  const [looks, setLooks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterTab, setFilterTab] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedLook, setSelectedLook] = useState(null);

  // Filters
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [selectedStaff, setSelectedStaff] = useState("all");
  const [locations, setLocations] = useState([]);
  const [staff, setStaff] = useState([]);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      window.location.href = "/login";
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, []);

  const getApiBaseUrl = useCallback(() => {
    return process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
  }, []);

  const loadLooks = useCallback(async () => {
    const headers = getAuthHeaders();
    const baseURL = getApiBaseUrl();
    if (!headers || !baseURL) return;

    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (filterTab !== "all") params.append("status", filterTab);
      if (selectedLocation !== "all")
        params.append("locationId", selectedLocation);
      if (selectedStaff !== "all") params.append("staffId", selectedStaff);

      const res = await fetch(
        `${baseURL}/api/looks/business/all?${params.toString()}`,
        {
          headers,
        },
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to load looks");

      setLooks(data.looks || []);
      setStats(data.stats || {});
    } catch (err) {
      setError(err.message || "Failed to load looks");
    } finally {
      setLoading(false);
    }
  }, [
    filterTab,
    selectedLocation,
    selectedStaff,
    getAuthHeaders,
    getApiBaseUrl,
  ]);

  const loadLocations = useCallback(async () => {
    const headers = getAuthHeaders();
    const baseURL = getApiBaseUrl();
    if (!headers || !baseURL) return;

    try {
      const res = await fetch(`${baseURL}/api/shop/my-shops`, { headers });
      const data = await res.json();
      if (res.ok) {
        const shops = data.shops || [];
        setLocations(shops);

        // Fetch staff for all locations and tag each with their shopId
        const allStaff = [];
        for (const shop of shops) {
          try {
            const staffRes = await fetch(
              `${baseURL}/api/shops/${shop._id}/staff`,
              { headers },
            );
            const staffData = await staffRes.json();
            if (staffRes.ok && staffData.staff) {
              // Tag each staff member with their shopId for filtering
              // Exclude R7 (booth renters) - they are independent and not part of business staff
              const staffWithShopId = staffData.staff
                .filter((s) => s.role !== "R7")
                .map((s) => ({
                  ...s,
                  shopId: shop._id,
                }));
              allStaff.push(...staffWithShopId);
            }
          } catch (err) {
          }
        }
        setStaff(allStaff);
      }
    } catch (err) {
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  const loadStaff = useCallback(async () => {
    // Staff loading is now handled in loadLocations
    // Keep this function for compatibility
  }, []);

  const openDetail = useCallback(
    async (lookId) => {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      try {
        const res = await fetch(`${baseURL}/api/looks/${lookId}`, { headers });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Failed to load");
        setSelectedLook(data.look);
      } catch (err) {
        alert(err.message);
      }
    },
    [getAuthHeaders, getApiBaseUrl],
  );

  useEffect(() => {
    loadLooks();
  }, [loadLooks]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  // Reset staff filter when location changes
  useEffect(() => {
    setSelectedStaff("all");
  }, [selectedLocation]);

  // Filter staff based on selected location
  const filteredStaff =
    selectedLocation === "all"
      ? staff
      : staff.filter((s) => s.shopId === selectedLocation);

  const filteredLooks = looks.filter((look) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      look.tags?.some((t) => t.toLowerCase().includes(s)) ||
      look.bookingSnapshot?.customerName?.toLowerCase().includes(s) ||
      look._id?.toLowerCase().includes(s) ||
      look.recordId?.toLowerCase().includes(s)
    );
  });

  return (
    <SidebarLayout>
      <div
        className="looks-page"
        style={{ maxWidth: "1400px", margin: "0 auto", padding: "24px" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "24px",
          }}
        >
          <FaImages size={28} color="#00509d" />
          <h2
            style={{
              margin: 0,
              fontSize: "26px",
              fontWeight: "700",
              color: "#111",
            }}
          >
            All Looks
          </h2>
        </div>

        {stats && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            <div
              style={{
                padding: "16px",
                background: "#f9fafb",
                borderRadius: "12px",
                border: "1px solid #e5e7eb",
              }}
            >
              <div
                style={{
                  fontSize: "13px",
                  color: "#6b7280",
                  marginBottom: "6px",
                }}
              >
                Total Looks
              </div>
              <div
                style={{ fontSize: "28px", fontWeight: "700", color: "#111" }}
              >
                {stats.total || 0}
              </div>
            </div>
            <div
              style={{
                padding: "16px",
                background: "#f0fdf4",
                borderRadius: "12px",
                border: "1px solid #86efac",
              }}
            >
              <div
                style={{
                  fontSize: "13px",
                  color: "#166534",
                  marginBottom: "6px",
                }}
              >
                Published
              </div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  color: "#166534",
                }}
              >
                {stats.published || 0}
              </div>
            </div>
            <div
              style={{
                padding: "16px",
                background: "#fffbeb",
                borderRadius: "12px",
                border: "1px solid #fcd34d",
              }}
            >
              <div
                style={{
                  fontSize: "13px",
                  color: "#92400e",
                  marginBottom: "6px",
                }}
              >
                Awaiting Consent
              </div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  color: "#92400e",
                }}
              >
                {stats.awaitingConsent || 0}
              </div>
            </div>
            <div
              style={{
                padding: "16px",
                background: "#f9fafb",
                borderRadius: "12px",
                border: "1px solid #e5e7eb",
              }}
            >
              <div
                style={{
                  fontSize: "13px",
                  color: "#6b7280",
                  marginBottom: "6px",
                }}
              >
                Archived
              </div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  color: "#6b7280",
                }}
              >
                {stats.archived || 0}
              </div>
            </div>
            {stats.reported > 0 && (
              <div
                style={{
                  padding: "16px",
                  background: "#fee2e2",
                  borderRadius: "12px",
                  border: "1px solid #fca5a5",
                }}
              >
                <div
                  style={{
                    fontSize: "13px",
                    color: "#991b1b",
                    marginBottom: "6px",
                  }}
                >
                  Reported
                </div>
                <div
                  style={{
                    fontSize: "28px",
                    fontWeight: "700",
                    color: "#991b1b",
                  }}
                >
                  {stats.reported || 0}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div
          style={{
            marginBottom: "24px",
            padding: "20px",
            background: "white",
            borderRadius: "12px",
            border: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "16px",
            }}
          >
            <FaFilter size={14} color="#6b7280" />
            <h3
              style={{
                margin: 0,
                fontSize: "16px",
                fontWeight: "600",
                color: "#374151",
              }}
            >
              Filters
            </h3>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "16px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#374151",
                  marginBottom: "6px",
                }}
              >
                Location
              </label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                <option value="all">All Locations</option>
                {locations.map((loc) => (
                  <option key={loc._id} value={loc._id}>
                    {loc.name}
                    {loc.city && loc.state
                      ? ` - ${loc.city}, ${loc.state}`
                      : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#374151",
                  marginBottom: "6px",
                }}
              >
                Professional
              </label>
              <select
                value={selectedStaff}
                onChange={(e) => setSelectedStaff(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                <option value="all">All Professionals</option>
                {filteredStaff.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.userId?.firstName} {s.userId?.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#374151",
                  marginBottom: "6px",
                }}
              >
                Search
              </label>
              <input
                type="text"
                placeholder="Search tags, customer..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            marginBottom: "20px",
            borderBottom: "2px solid #e5e7eb",
            paddingBottom: "0",
          }}
        >
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilterTab(tab.key)}
              style={{
                padding: "10px 20px",
                background: filterTab === tab.key ? "#eff6ff" : "transparent",
                border: "none",
                borderBottom:
                  filterTab === tab.key
                    ? "2px solid #3b82f6"
                    : "2px solid transparent",
                marginBottom: "-2px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: filterTab === tab.key ? "700" : "500",
                color: filterTab === tab.key ? "#1e40af" : "#6b7280",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
          <button
            onClick={loadLooks}
            disabled={loading}
            style={{
              padding: "10px 20px",
              background: "white",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FaRedo size={13} /> Refresh
          </button>
        </div>

        {/* Error */}
        {error && (
          <div
            style={{
              padding: "16px",
              background: "#fee2e2",
              border: "1px solid #fca5a5",
              borderRadius: "8px",
              color: "#991b1b",
              marginBottom: "20px",
            }}
          >
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div
            style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}
          >
            Loading looks...
          </div>
        )}

        {/* Looks Grid */}
        {!loading && filteredLooks.length === 0 && (
          <div
            style={{ textAlign: "center", padding: "60px", color: "#9ca3af" }}
          >
            <FaImages size={48} color="#d1d5db" />
            <p style={{ marginTop: "16px", fontSize: "16px" }}>
              No looks found
            </p>
          </div>
        )}

        {!loading && filteredLooks.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: "20px",
            }}
          >
            {filteredLooks.map((look) => {
              const statusInfo = STATUS_LABELS[look.status] || {};
              const imageUrl =
                look.image?.url ||
                look.images?.[0]?.publicUrl ||
                look.images?.[0]?.url;

              return (
                <div
                  key={look._id}
                  style={{
                    background: "white",
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    overflow: "hidden",
                    cursor: "pointer",
                    transition: "box-shadow 0.2s",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(0,0,0,0.1)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  {imageUrl && (
                    <div
                      style={{
                        width: "100%",
                        height: "200px",
                        background: "#f3f4f6",
                        position: "relative",
                      }}
                    >
                      <img
                        src={imageUrl}
                        alt="Look"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      {look.reporting?.isReported && (
                        <div
                          style={{
                            position: "absolute",
                            top: "8px",
                            right: "8px",
                            padding: "4px 8px",
                            background: "#fee2e2",
                            borderRadius: "6px",
                            fontSize: "11px",
                            fontWeight: "600",
                            color: "#991b1b",
                          }}
                        >
                          Reported
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ padding: "16px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 10px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: statusInfo.color,
                          background: statusInfo.bg,
                        }}
                      >
                        {statusInfo.label}
                      </span>
                    </div>
                    {look.bookingSnapshot?.customerName && (
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#6b7280",
                          marginBottom: "4px",
                        }}
                      >
                        Customer: {look.bookingSnapshot.customerName}
                      </div>
                    )}
                    {look.tags && look.tags.length > 0 && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#9ca3af",
                          marginBottom: "8px",
                        }}
                      >
                        Tags: {look.tags.join(", ")}
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#9ca3af",
                        marginBottom: "4px",
                      }}
                    >
                      {new Date(look.createdAt).toLocaleDateString()}
                    </div>
                    <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "2px" }}>
                      Record ID: {look.recordId || look._id}
                    </div>
                    <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "12px" }}>
                      Unique views: {(look.analytics?.viewCount || 0).toLocaleString()} · Clicks: {(look.analytics?.clickCount || 0).toLocaleString()} · Ratio: {look.analytics?.viewCount > 0 ? `${((look.analytics?.clickCount || 0) / look.analytics.viewCount).toFixed(1)}x` : "1.0x"}
                    </div>
                    <button
                      onClick={() => openDetail(look._id)}
                      style={{
                        width: "100%",
                        padding: "8px",
                        background: "#eff6ff",
                        border: "1px solid #3b82f6",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#1e40af",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                    >
                      <FaEye size={12} /> View Details
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Look Detail Modal */}
        {selectedLook && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              padding: "20px",
            }}
            onClick={() => setSelectedLook(null)}
          >
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                maxWidth: "700px",
                width: "100%",
                maxHeight: "90vh",
                overflowY: "auto",
                padding: "30px",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3
                style={{
                  margin: "0 0 20px 0",
                  fontSize: "20px",
                  fontWeight: "700",
                }}
              >
                Look Details
              </h3>
              {selectedLook.image?.url && (
                <img
                  src={selectedLook.image.url}
                  alt="Look"
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    marginBottom: "20px",
                  }}
                />
              )}
              <div style={{ display: "grid", gap: "12px", fontSize: "14px" }}>
                <div>
                  <strong>Status:</strong>{" "}
                  {STATUS_LABELS[selectedLook.status]?.label}
                </div>
                <div>
                  <strong>Consent:</strong> {selectedLook.consent?.status}
                </div>
                {selectedLook.consent?.approvedAt && (
                  <div>
                    <strong>Consent Approved At:</strong>{" "}
                    {new Date(selectedLook.consent.approvedAt).toLocaleString()}
                  </div>
                )}
                <div>
                  <strong>Record ID:</strong> {selectedLook.recordId || selectedLook._id}
                </div>
                <div>
                  <strong>Unique Views:</strong>{" "}
                  {(selectedLook.analytics?.viewCount || 0).toLocaleString()}
                </div>
                <div>
                  <strong>Total Clicks:</strong>{" "}
                  {(selectedLook.analytics?.clickCount || 0).toLocaleString()}
                </div>
                <div>
                  <strong>Click/Unique Ratio:</strong>{" "}
                  {selectedLook.analytics?.viewCount > 0
                    ? `${((selectedLook.analytics?.clickCount || 0) / selectedLook.analytics.viewCount).toFixed(1)}x`
                    : "1.0x"}
                </div>
                {selectedLook.bookingSnapshot?.customerName && (
                  <div>
                    <strong>Customer:</strong>{" "}
                    {selectedLook.bookingSnapshot.customerName}
                  </div>
                )}
                {selectedLook.tags && selectedLook.tags.length > 0 && (
                  <div>
                    <strong>Tags:</strong> {selectedLook.tags.join(", ")}
                  </div>
                )}
                <div>
                  <strong>Created:</strong>{" "}
                  {new Date(selectedLook.createdAt).toLocaleString()}
                </div>
                {selectedLook.publishedAt && (
                  <div>
                    <strong>Published:</strong>{" "}
                    {new Date(selectedLook.publishedAt).toLocaleString()}
                  </div>
                )}
              </div>
              <button
                onClick={() => setSelectedLook(null)}
                style={{
                  marginTop: "24px",
                  width: "100%",
                  padding: "12px",
                  background: "#3b82f6",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "white",
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </SidebarLayout>
  );
}

export default BusinessLooksPage;
